import {HeroUIProvider} from "@heroui/react";
import {ToastProvider} from "@heroui/toast";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import type {NavigateOptions, ToOptions} from '@tanstack/react-router';
import {Outlet, useRouter} from "@tanstack/react-router";
import {getAnalytics, logEvent} from "firebase/analytics";
import Pusher from "pusher-js";
import React, {useEffect} from "react";
import OutbrainPixel from "./components/pixels/OutbrainPixel.tsx";
import {config} from "./config/config.ts";
import {GlobalModalProvider} from "./core/GlobalModal.tsx";
import {PusherProvider} from "./core/PusherContext.tsx";
import {useJwtAuth, useJwtUser} from "./JWTAuthProvider.tsx";

// const BlogPage = lazy(() => import( "./landing/BlogPage.tsx"));
// const ContactPage = lazy(() => import( "./landing/ContactPage.tsx"));
// const AboutPage = lazy(() => import( "./landing/AboutPage.tsx"));
// const FaqPage = lazy(() => import( "./landing/FaqPage.tsx"));
// const LegalPage = lazy(() => import('./landing/LegalPage.tsx'));
//
// const App = lazy(() => import('./App.tsx'));

declare module "@react-types/shared" {
    interface RouterConfig {
        href: ToOptions['to'];
        routerOptions: Omit<NavigateOptions, keyof ToOptions>;
    }
}

function MyPageViewLogger() {
    const analytics = getAnalytics();

    useEffect(() => {
        logEvent(analytics, 'page_view', {page_location: location.href});
    }, [location.href]);

    return null;
}

const queryClient = new QueryClient();

const AppThemed = () => {
    const jwt = useJwtAuth();
    const user = useJwtUser();
    const router = useRouter();

    queryClient.setDefaultOptions({
        queries:   {
            // enabled: !!user?.uid,
        },
        mutations: {
            onError: (error: Error) => console.error('[MutationError]', error),
        },
    })

    const pusher = new Pusher('ws-app', {
        cluster:              '',
        wsHost:               config.pusherHost,
        forceTLS:             config.pusherForceTls,
        enableStats:          false,
        enabledTransports:    ['ws', 'wss'],
        userAuthentication:   {
            endpoint:  config.apiUrl + "/reverb-auth:user" + (document.cookie.indexOf('XDEBUG_SESSION') > -1 ? config.debugParams : ''),
            transport: 'ajax'
        },
        channelAuthorization: {
            transport: 'ajax',
            endpoint:  config.apiUrl + "/reverb-auth" + (document.cookie.indexOf('XDEBUG_SESSION') > -1 ? config.debugParams : ''),
            headers() {
                return {
                    'x-firebase-auth': (user?.['stsTokenManager'])?.accessToken,
                }
            },
        },
    });

    useEffect(() => {
        if (!jwt.initialized) {
            return;
        }

        const scriptId = '#adsbygoogle-ca-pub-4675876197200441';
        if (!localStorage.disable_ads && !user?.is_subscribed && !user?.meta?.disable_ads && !document.querySelector(scriptId)) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.async = true;
            script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4675876197200441";
            script.crossOrigin = 'anonymous';
            script.onload = function () {
                (window['adsbygoogle'] = window['adsbygoogle'] || []).push({});
            }
            document.head.appendChild(script);
        }

        document.querySelector('html')?.classList.toggle('user-subscribed', user?.is_subscribed);

    }, [jwt.initialized, user]);

    return (
        <QueryClientProvider client={queryClient}>
            <GlobalModalProvider>
                <PusherProvider pusher={pusher}>
                    <HeroUIProvider
                        navigate={(to, options) => router.navigate({to, ...options})}
                        useHref={(to) => router.buildLocation({to}).href}>
                        <ToastProvider
                            toastProps={{timeout: 3000, shouldShowTimeoutProgress: true,}}
                            placement="bottom-right"
                            toastOffset={70}
                        />
                        <OutbrainPixel />

                        <Outlet />

                        {/*<Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/pricing/" element={<PricingPage />} />
                    <Route path="/faq/" element={<FaqPage />} />
                    <Route path="/contact/" element={<ContactPage />} />
                    <Route path="/about/" element={<AboutPage />} />
                    <Route path="/privacy-legacy/" element={<LegalPage />} />
                    <Route path="/privacy/" element={<LegalPage />} />
                    <Route path="/cookie-policy/" element={<LegalPage />} />
                    <Route path="/terms/" element={<LegalPage />} />
                    <Route path="/blog/:post_slug?" element={<BlogPage />} />

                    <Route path="/login/:subpage?" element={<LoginView />} />
                    <Route path="/signup/:signup_type?" element={<SignupView />} />
                    <Route path="/confirm-email/" element={<ConfirmEmailView />} />

                    <Route path="/return/:subpage" element={<ReturnPage />} />
                    <Route path="/return/:subpage" element={<ReturnPage />} />

                    <Route path="/*" element={<App />} />
                </Routes>*/}

                        <ins className="adsbygoogle"
                             style={{display: "block"}}
                             data-ad-client="ca-pub-4675876197200441"
                             data-ad-slot="3709662177"
                             data-ad-format="auto"
                             data-full-width-responsive="true"></ins>

                        {process.env.NODE_ENV === 'production' && <MyPageViewLogger />}
                    </HeroUIProvider>
                </PusherProvider>
            </GlobalModalProvider>

            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

export default AppThemed;
